/**
 * Embeddable Chat Page
 *
 * This is a minimal version of the chat page designed for iframe embedding.
 * Features:
 * - postMessage communication with parent window
 * - Customizable via URL parameters
 * - Optional sidebar via ?sidebar=true
 * - Clean, frameless design
 */

import { chatSidebarOpenAtom } from '@/atoms/chatSidebarAtoms';
import { clearExternalLaunchAuth } from '@/atoms/externalLaunchAuthAtoms';
import { ChatRenderer } from '@/components/chat/Chat';
import { ChatSidebar } from '@/components/chat/components/ChatSidebar';
import { EvidenceChatSkeleton } from '@/components/chat/components/EvidenceChatSkeleton';
import { ChatSidebarWork } from '@/components/layout/Navigation/ChatSidebarWork';
import { useEmbedMessaging } from '@/hooks/useEmbedMessaging';
import { useIsMobile } from '@/hooks/useWindowDimensions';
import { type Dispatch } from '@/store';
import { getCurrentChatId } from '@/utils/currentChatId';
// The state door, not the `@/lib/auth` barrel — the barrel pulls in
// `getAccessToken.ts`, which imports `@/store` and creates the circular this
// file already dodges via a lazy `import('@/lib/auth')` below.
import { authCacheSuppressedAtom } from '@heidi/auth/state';
import { useAtom, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

const EmbedChatPage = () => {
  const router = useRouter();
  const dispatch = useDispatch<Dispatch>();
  const [isReady, setIsReady] = useState(false);
  const [currentChatId, setCurrentChatId] = useState<string | undefined>(undefined);
  const [newChatCounter, setNewChatCounter] = useState(0);
  const [, setSidebarOpen] = useAtom(chatSidebarOpenAtom);
  const isMobile = useIsMobile();

  const setAuthCacheSuppressed = useSetAtom(authCacheSuppressedAtom);

  const { postToParent } = useEmbedMessaging({ notifyParentOnAuthActions: true });

  // Open sidebar by default on non-mobile
  useEffect(() => {
    if (!isMobile) {
      setSidebarOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // URL parameters for customization
  const chatIdParam = router.query.chatId as string | undefined;
  const initialQuery = router.query.query as string | undefined;
  const theme = router.query.theme as string | undefined;
  const hideHeader = router.query.hideHeader === 'true';

  // Use state for chatId so sidebar can update it
  const chatId = currentChatId ?? chatIdParam;
  const canViewChat = chatId === getCurrentChatId();

  // Enforce the embed widget's public/unauthenticated UI contract.
  //
  // Suppress the token cache FIRST — before clearing Redux, before
  // clearing the cache — so the moment this page has mounted, any
  // subsequent `getAccessToken()` (scheduler focus refetch, axios,
  // direct caller) returns null immediately without hitting the
  // provider and without writing atoms. Without the suppression, the
  // initial clear held only by accident: a later window-focus event
  // would re-fetch off the parent session's same-origin cookies,
  // atoms would flip to `'authenticated'`, and the token would be
  // mirrored back to Redux — re-authenticating a widget whose
  // contract is public.
  //
  // Cleanup MUST reset the atom to `false`. A leaked `true` leaves the
  // app-wide cache dead across client-side navigation until reload.
  useEffect(() => {
    setAuthCacheSuppressed(true);
    dispatch.auth.clear();
    // Clear any FHIR-launch / MD-restored external token so the embed widget
    // cannot accidentally re-attach it to its own requests. It is never
    // persisted, but in-memory state survives client-side navigations without
    // this explicit clear.
    clearExternalLaunchAuth();
    // Lazy import dodges the static circular through
    // `@/store -> authModel -> @/lib/auth`.
    void (async () => {
      const { clearAuthCache } = await import('@/lib/auth');
      clearAuthCache({ reason: 'logout' });
    })();
    return () => {
      setAuthCacheSuppressed(false);
    };
  }, [dispatch, setAuthCacheSuppressed]);

  // Initialize chatId from URL param
  useEffect(() => {
    if (chatIdParam && !currentChatId) {
      setCurrentChatId(chatIdParam);
    }
  }, [chatIdParam, currentChatId]);

  // Notify parent when ready
  useEffect(() => {
    if (router.isReady) {
      setIsReady(true);
      postToParent({ type: 'heidi:ready' });
    }
  }, [router.isReady, postToParent]);

  // Auto-resize iframe based on content
  useEffect(() => {
    if (!isReady) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        postToParent({
          type: 'heidi:resize',
          height: entry.contentRect.height,
        });
      }
    });

    resizeObserver.observe(document.body);
    return () => resizeObserver.disconnect();
  }, [isReady, postToParent]);

  // Apply theme class - default to light for embed
  const themeClass = theme === 'dark' ? 'dark' : '';
  const bgColor = theme === 'dark' ? '#1a1a2e' : '#ffffff';

  // Sidebar handlers
  const handleSelectChat = (selectedChatId: string) => {
    setCurrentChatId(selectedChatId || undefined);
    // Update URL without full page reload
    const params = new URLSearchParams();
    if (selectedChatId) params.set('chatId', selectedChatId);
    if (theme) params.set('theme', theme);
    if (hideHeader) params.set('hideHeader', 'true');
    const queryString = params.toString();
    const newUrl = `/chat/embed${queryString ? `?${queryString}` : ''}`;
    window.history.replaceState({}, '', newUrl);
  };

  const handleNewChat = () => {
    setCurrentChatId(undefined);
    setNewChatCounter((prev) => prev + 1); // Force ChatRenderer remount
    const params = new URLSearchParams();
    if (theme) params.set('theme', theme);
    if (hideHeader) params.set('hideHeader', 'true');
    const queryString = params.toString();
    const newUrl = `/chat/embed${queryString ? `?${queryString}` : ''}`;
    window.history.replaceState({}, '', newUrl);
  };

  const handleDeleteChat = (deletedChatId: string) => {
    if (chatId === deletedChatId) {
      handleNewChat();
    }
  };

  return (
    <div
      className={`flex h-screen w-screen flex-col ${themeClass}`}
      style={{
        margin: 0,
        padding: 0,
        overflow: 'hidden',
        backgroundColor: bgColor,
      }}
    >
      {!router.isReady ? (
        <EvidenceChatSkeleton />
      ) : (
        <div className="flex size-full">
          <ChatSidebarWork>
            {(work) => (
              <ChatSidebar
                onSelectChat={handleSelectChat}
                onNewChat={handleNewChat}
                onDeleteChat={handleDeleteChat}
                currentChatId={chatId}
                work={work}
              />
            )}
          </ChatSidebarWork>
          <div className="flex flex-1 flex-col">
            <ChatRenderer
              key={chatId ?? `new-chat-${newChatCounter}`}
              sessionId={null}
              chatId={chatId ?? null}
              canViewChatHistory={canViewChat}
              initialQuery={initialQuery}
              hideHeader={hideHeader}
              isWidget
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default EmbedChatPage;
